import CurrencyFormatter from "currency-formatter";
import GeneralHelper from "../../utils/GeneralHelper";
import {store} from "../../store";

export class ReconciliationValuationMethodsReportGroupItem {
  /**
   *
   * @param {string} name
   * @param {number} value
   * @param {number} weight
   * @param {number} summary
   */
  constructor({
    groupItemKey,
    allowInput,
    name,
    value = 0,
    weight = 0
  }) {
    this.groupItemKey = groupItemKey
    this.allowInput = allowInput
    this.name = name
    this.value = value
    this.weight = weight
  }

  static getAllowedReportGroupItemKeys() {
    return {
      ORDERLY_LIQUIDATION_VALUE: "ORDERLY_LIQUIDATION_VALUE",
      BOOK_VALUE: "BOOK_VALUE",
      ADJUSTED_NET_BOOK_VALUE: "ADJUSTED_NET_BOOK_VALUE",
      CAPITALIZED_EARNINGS: "CAPITALIZED_EARNINGS",
      DISCOUNTED_CASH_FLOW_METHOD: "DISCOUNTED_CASH_FLOW_METHOD",
      PRATT_MULTIPLE_OF_DISCRETIONARY_EARNINGS: "PRATT_MULTIPLE_OF_DISCRETIONARY_EARNINGS",
      SHILTZ_CAPITALIZATION_TABLE: "SHILTZ_CAPITALIZATION_TABLE",
      REGRESSION_ANALYSIS: "REGRESSION_ANALYSIS",
      RULE_OF_THUMB_REFERENCE_GUIDE: "RULE_OF_THUMB_REFERENCE_GUIDE"
    }
  }

  get floatValue() {
    return CurrencyFormatter.unformat(this.value, { code: "USD" })
  }

  get floatWeight() {
    return CurrencyFormatter.unformat(this.weight, { code: "USD" })
  }

  get summary() {
    return (this.floatValue * this.weight) / 100
  }

  get formattedSummary() {
    return CurrencyFormatter.format(this.summary, { code: "USD", symbol: "$ " })
  }

  get formattedValue() {
    return CurrencyFormatter.format(this.value, { code: "USD", symbol: "$ " })
  }
}

export class ReconciliationValuationMethodsReportGroup {
  /**
   *
   * @param {string} groupName
   * @param {array<ReconciliationValuationMethodsReportGroupItem>} groupItems
   */
  constructor({
    groupKey,
    groupName,
    groupItems
  }) {
    this.groupKey = groupKey
    this.groupName = groupName
    this.groupItems = groupItems
  }

  static getAllowedReportGroupKeys() {
    return {
      ASSET_BASED_APPROACH: "ASSET_BASED_APPROACH",
      INCOME_BASED_APPROACH: "INCOME_BASED_APPROACH",
      MARKET_BASED_APPROACH: "MARKET_BASED_APPROACH"
    }
  }
}

export class MinorityShareholderDiscount {
  constructor({
    ownershipInterestPercentage,
    discountForControlPercentage,
    discountForLackOfMarketability,
    middleNote = "",
    finalNote = ""
  }) {
    this.ownershipInterestPercentage = ownershipInterestPercentage
    this.discountForControlPercentage = discountForControlPercentage
    this.discountForLackOfMarketability = discountForLackOfMarketability
    this.middleNote = middleNote
    this.finalNote = finalNote
  }

  get ownershipInterestPercentageFloat() {
    return CurrencyFormatter.unformat(this.ownershipInterestPercentage, { code: "USD" })
  }

  get discountForControlPercentageFloat() {
    return CurrencyFormatter.unformat(this.discountForControlPercentage, { code: "USD" })
  }

  get discountForLackOfMarketabilityFloat() {
    return CurrencyFormatter.unformat(this.discountForLackOfMarketability, { code: "USD" })
  }
}

export default class ReconciliationValuationMethodsReport {
  /**
   *
   * @param {array<ReconciliationValuationMethodsReportGroup>} reportGroups
   * @param {number} excessAssetsOrLiabilities
   * @param {number} totalWeight
   * @param {number} totalSummary
   * @param {MinorityShareholderDiscount} minorityShareholderDiscount
   */
  constructor({
    reportGroups,
    excessAssetsOrLiabilities,
    totalLiabilitiesAdjustedValue,
    totalWeight= 0,
    totalSummary = 0,
    minorityShareholderDiscount
  }) {
    this.reportGroups = reportGroups
    this.excessAssetsOrLiabilities = excessAssetsOrLiabilities
    this.totalLiabilitiesAdjustedValue = totalLiabilitiesAdjustedValue
    this.minorityShareholderDiscount = minorityShareholderDiscount
  }

  get formattedExcessAssetsOrLiabilities() {
    return CurrencyFormatter.format(this.excessAssetsOrLiabilities, { code: "USD", symbol: "$ " })
  }

  get totalWeight() {
    const reportGroupItems = []
    this.reportGroups.forEach((reportGroup) => {
      reportGroup.groupItems.forEach((groupItem) => reportGroupItems.push(groupItem))
    })

    return GeneralHelper.sumObjectAttributeFromArray(reportGroupItems, "floatWeight")
  }

  get formattedTotalWeight() {
    return CurrencyFormatter.format(this.totalWeight, { code: "USD", symbol: "" })
  }

  get totalSummary() {
    const reportGroupItems = []
    this.reportGroups.forEach((reportGroup) => {
      reportGroup.groupItems.forEach((groupItem) => reportGroupItems.push(groupItem))
    })

    const totalSummary = GeneralHelper.sumObjectAttributeFromArray(reportGroupItems, "summary")

    return totalSummary + this.excessAssetsOrLiabilities
  }

  get formattedTotalSummary() {
    return CurrencyFormatter.format(this.totalSummary, { code: "USD", symbol: "$ " })
  }

  get formattedAdjustedLiabilities() {
    return CurrencyFormatter.format(this.totalLiabilitiesAdjustedValue, { code: "USD", symbol: "$ " })
  }

  get totalEquitySummary() {
    return this.totalSummary - this.totalLiabilitiesAdjustedValue;
  }

  get formattedtotalEquitySummary() {
    return CurrencyFormatter.format(this.totalEquitySummary, { code: "USD", symbol: "$ " })
  }

  get ownershipInterestAmount() {
    return this.totalEquitySummary * (this.minorityShareholderDiscount?.ownershipInterestPercentageFloat / 100)
  }

  get discountForControlAmount() {
    return this.ownershipInterestAmount * (1 - this.minorityShareholderDiscount?.discountForControlPercentageFloat / 100)
  }

  get discountForLackOfMarketabilityAmount() {
    return this.discountForControlAmount * (1 - this.minorityShareholderDiscount?.discountForLackOfMarketabilityFloat / 100)
  }

  get minorityShareholderDiscountDeterminedFinalValue() {
    return this.discountForLackOfMarketabilityAmount;
  }

  get ownershipInterestAmountFormatted() {
    return CurrencyFormatter.format(this.ownershipInterestAmount, { code: "USD", symbol: "$" })
  }

  get discountForControlAmountFormatted() {
    return CurrencyFormatter.format(this.discountForControlAmount, {  code: "USD", symbol: "$"})
  }

  get discountForLackOfMarketabilityAmountFormatted() {
    return CurrencyFormatter.format(this.discountForLackOfMarketabilityAmount, { code: "USD", symbol: "$" })
  }

  get minorityShareholderDiscountDeterminedFinalValueFormatted() {
    return CurrencyFormatter.format(this.minorityShareholderDiscountDeterminedFinalValue, { code: "USD", symbol: "$" })
  }

  getGroupItem({ groupKey, groupItemKey }) {
    const groupItem = this.reportGroups.filter((reportGroup) => reportGroup.groupKey === groupKey)[0].groupItems.find((groupItem) => {
      return groupItem?.groupItemKey === groupItemKey
    })

    if (!groupItem) return 0

    return groupItem
  }

  /**
   *
   * @param {BalanceSheet} balanceSheet
   */
  static generateReport({
    totalLiquidationValue,
    totalEquityBalance,
    totalEquityAdjustedValue,
    totalLiabilitiesAdjustedValue,
    afterTaxCapitalizedEarningsOfSubjectCompany,
    valueOfSubjectCompany,
    totalExcessAssetsOrLiabilities,
    reconciliationOfValuationMethodsReport
  }) {
    const {
      ASSET_BASED_APPROACH,
      INCOME_BASED_APPROACH,
      MARKET_BASED_APPROACH
    } = ReconciliationValuationMethodsReportGroup.getAllowedReportGroupKeys()

    const {
      ORDERLY_LIQUIDATION_VALUE,
      BOOK_VALUE,
      ADJUSTED_NET_BOOK_VALUE,
      CAPITALIZED_EARNINGS,
      DISCOUNTED_CASH_FLOW_METHOD,
      PRATT_MULTIPLE_OF_DISCRETIONARY_EARNINGS,
      SHILTZ_CAPITALIZATION_TABLE,
      REGRESSION_ANALYSIS,
      RULE_OF_THUMB_REFERENCE_GUIDE,
    } = ReconciliationValuationMethodsReportGroupItem.getAllowedReportGroupItemKeys()

    // Generate report groups
     const regressionAnalysisGroupItem = reconciliationOfValuationMethodsReport ? (
        reconciliationOfValuationMethodsReport.getGroupItem({
          groupKey: MARKET_BASED_APPROACH,
          groupItemKey: REGRESSION_ANALYSIS
        })
    ) :  0

    const ruleOfThumbReferenceGuideGroupItem = reconciliationOfValuationMethodsReport ? (
        reconciliationOfValuationMethodsReport.getGroupItem({
          groupKey: MARKET_BASED_APPROACH,
          groupItemKey: RULE_OF_THUMB_REFERENCE_GUIDE
        })
    ) :  0


    const reportGroups = [
      new ReconciliationValuationMethodsReportGroup({
        groupKey: ASSET_BASED_APPROACH,
        groupName: "Asset based approach",
        groupItems: [
            new ReconciliationValuationMethodsReportGroupItem({
              groupItemKey: ORDERLY_LIQUIDATION_VALUE,
              name: "Orderly Liquidation Value",
              value: totalLiquidationValue,
              weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport?.getGroupItem({
                    groupKey: ASSET_BASED_APPROACH,
                    groupItemKey: ORDERLY_LIQUIDATION_VALUE
                  }).weight
              ) : 0
            }),
            new ReconciliationValuationMethodsReportGroupItem({
              groupItemKey: BOOK_VALUE,
              name: "Book Value",
              value: totalEquityBalance,
              weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport?.getGroupItem({
                    groupKey: ASSET_BASED_APPROACH,
                    groupItemKey: BOOK_VALUE
                  }).weight
              ) : 0
            }),
            new ReconciliationValuationMethodsReportGroupItem({
              groupItemKey: ADJUSTED_NET_BOOK_VALUE,
              name: "Adjusted Net Book Value",
              value: totalEquityAdjustedValue,
              weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport?.getGroupItem({
                    groupKey: ASSET_BASED_APPROACH,
                    groupItemKey: ADJUSTED_NET_BOOK_VALUE
                  }).weight
              ) : 0
            })
          ]
      }),
      new ReconciliationValuationMethodsReportGroup({
        groupKey: INCOME_BASED_APPROACH,
        groupName: "Income based approach",
        groupItems: [
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: CAPITALIZED_EARNINGS,
            name: "Capitalized Earnings",
            value: afterTaxCapitalizedEarningsOfSubjectCompany,
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: INCOME_BASED_APPROACH,
                    groupItemKey: CAPITALIZED_EARNINGS
                  }).weight
              ) : 0
          }),
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: DISCOUNTED_CASH_FLOW_METHOD,
            name: "Discounted Cash Flow Method",
            value: valueOfSubjectCompany === Infinity ? 0 : valueOfSubjectCompany,
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: INCOME_BASED_APPROACH,
                    groupItemKey: ORDERLY_LIQUIDATION_VALUE
                  }).weight
              ) : 0
          }),
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: PRATT_MULTIPLE_OF_DISCRETIONARY_EARNINGS,
            name: "Pratt Multiple of Discretionary Earnings",
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: INCOME_BASED_APPROACH,
                    groupItemKey: PRATT_MULTIPLE_OF_DISCRETIONARY_EARNINGS
                  }).weight
              ) : 0
          }),
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: SHILTZ_CAPITALIZATION_TABLE,
            name: "Shiltz Capitalization Table",
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: INCOME_BASED_APPROACH,
                    groupItemKey: SHILTZ_CAPITALIZATION_TABLE
                  }).weight
              ) : 0
          })
        ]
      }),
      new ReconciliationValuationMethodsReportGroup({
        groupKey: MARKET_BASED_APPROACH,
        groupName: "Market based approach",
        groupItems: [
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: REGRESSION_ANALYSIS,
            allowInput: true,
            name: "Regression Analysis",
            value: regressionAnalysisGroupItem ? regressionAnalysisGroupItem.value : 0,
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: MARKET_BASED_APPROACH,
                    groupItemKey: REGRESSION_ANALYSIS
                  }).weight
              ) : 0
          }),
          new ReconciliationValuationMethodsReportGroupItem({
            groupItemKey: RULE_OF_THUMB_REFERENCE_GUIDE,
            allowInput: true,
            name: "Rule of Thumb Reference Guide",
            value: ruleOfThumbReferenceGuideGroupItem ? ruleOfThumbReferenceGuideGroupItem.value : 0,
            weight: reconciliationOfValuationMethodsReport ? (
                  reconciliationOfValuationMethodsReport.getGroupItem({
                    groupKey: MARKET_BASED_APPROACH,
                    groupItemKey: RULE_OF_THUMB_REFERENCE_GUIDE
                  }).weight
              ) : 0
          })
        ]
      })
    ]

    // Calculate total weight and total summary
    const reportGroupItems = []
    reportGroups.forEach(reportGroup => reportGroup.groupItems.forEach(groupItem => reportGroupItems.push(groupItem)))
    const totalWeight = GeneralHelper.sumObjectAttributeFromArray(reportGroupItems, "weight")
    const totalSummary = GeneralHelper.sumObjectAttributeFromArray(reportGroupItems, "summary")

    return new ReconciliationValuationMethodsReport({
      reportGroups,
      excessAssetsOrLiabilities: totalExcessAssetsOrLiabilities,
      totalLiabilitiesAdjustedValue,
      totalWeight: totalWeight,
      totalSummary: totalSummary,
      minorityShareholderDiscount: new MinorityShareholderDiscount({
        ...reconciliationOfValuationMethodsReport?.minorityShareholderDiscount
      })
    })
  }
}
